import { IItemSizeFormInput, ItemSizeProps } from "../types/item";
import { compareCodes, rateNumber, rateText } from "./processRate";

// Item sizes such as "94": a number in the size unit of the settings (shown as 94 mm), shared by all items. Not the
// material sizes. A few sizes added before 2026-10-25 may still be text with their own unit, such as "1/2 inch".
// The limit matches ItemsController::validatorRules().
export const maxItemSizes = 50;

// A size as it is saved: above 0, up to 8 whole digits and 2 decimals, with no leading zeros and no trailing decimal
// zeros, so one number has one name. Keep in step with Size::NUMBER_PATTERN on the backend.
const sizeNumberPattern = /^(0|[1-9]\d{0,7})(\.\d?[1-9])?$/;

export const isNumberSize = (name: string) =>
  sizeNumberPattern.test(name) && Number(name) > 0;

// Typed text as it is saved ("096." → "96", "12.50" → "12.5"), or "" when it isn't a size. The select only lets
// digits and one dot through.
export const typedSizeName = (text: string) => {
  const number = rateNumber(text);
  const name = number === null ? "" : String(number);

  return isNumberSize(name) ? name : "";
};

// A size with the size unit: "96 mm". A text size is shown as it was written.
export const sizeLabel = (name: string, unit: string) =>
  isNumberSize(name) ? `${name} ${unit}` : name;

// A size name as it is saved: no spaces around it, and single spaces inside.
const sizeName = (text: string) => text.trim().replace(/\s+/g, " ");

// Sizes that read the same are one size: names that differ only in case or spaces, like on the server, and a text
// "94 mm" from before sizes became numbers with the number 94 while the unit is mm.
export const sizeKey = (name: string, unit: string) =>
  sizeName(sizeLabel(name, unit)).toLowerCase();

// Reading order: names that start with a number by that number (12.25 before 12.5 before 94), then the rest.
// Keep in step with Size::compareNames on the backend.
export const compareSizes = (a: string, b: string) => {
  const numberA = a.match(/^\d+(\.\d+)?/);
  const numberB = b.match(/^\d+(\.\d+)?/);

  if (numberA && numberB && Number(numberA[0]) !== Number(numberB[0])) {
    return Number(numberA[0]) - Number(numberB[0]);
  }

  if (!numberA !== !numberB) return numberA ? -1 : 1;

  return compareCodes(a, b);
};

// One name per size in reading order. The first spelling given wins, so pass saved names first.
export const uniqueSizeNames = (names: string[], unit: string) => {
  const byKey = new Map<string, string>();

  names.forEach((name) => {
    const key = sizeKey(name, unit);
    if (key && !byKey.has(key)) byKey.set(key, name);
  });

  return Array.from(byKey.values()).sort(compareSizes);
};

export const emptySizeRow = (name: string): IItemSizeFormInput => ({
  name,
  cutting_rate: "",
  turning_rate: "",
  vmc_rate: "",
  cut_length: "",
});

// Saved sizes as form rows, in reading order. Mapped before sorting, since the saved list may be frozen store state.
export const sizeFormRows = (sizes: ItemSizeProps[] | undefined) =>
  (sizes ?? [])
    .map(
      (size): IItemSizeFormInput => ({
        name: size.size?.name ?? "",
        cutting_rate: rateText(size.cutting_rate),
        turning_rate: rateText(size.turning_rate),
        vmc_rate: rateText(size.vmc_rate),
        cut_length: rateText(size.cut_length),
      })
    )
    .filter((row) => row.name)
    .sort((a, b) => compareSizes(a.name, b.name));

// A sub item's size rows: the main item's sizes in its order, since sizes are picked on the main item only. Each keeps
// what the sub item has for that size; a size it doesn't have yet gets an empty row plus `fill`, such as its table's
// common rates. Rows match by sizeKey when the unit is given, else by name, as rows made from the main item's names
// carry the same text.
export const inheritedSizeRows = (
  mainRows: Pick<IItemSizeFormInput, "name">[] | undefined,
  rowRows: IItemSizeFormInput[] | undefined,
  fill: Partial<Omit<IItemSizeFormInput, "name">> = {},
  unit?: string
): IItemSizeFormInput[] => {
  const keyOf = (name: string) =>
    unit === undefined ? name : sizeKey(name, unit);
  const byKey = new Map((rowRows ?? []).map((row) => [keyOf(row.name), row]));

  return (mainRows ?? []).map(({ name }) => {
    const row = byKey.get(keyOf(name));
    return row ? { ...row, name } : { ...emptySizeRow(name), ...fill };
  });
};

// Whether two lists hold the same size names in the same order.
export const sameSizeNames = (
  a: Pick<IItemSizeFormInput, "name">[] | undefined,
  b: Pick<IItemSizeFormInput, "name">[] | undefined
) =>
  (a ?? []).length === (b ?? []).length &&
  (a ?? []).every((size, index) => size.name === b?.[index]?.name);

// Request body of an item's or sub item's sizes. The names were tidied when picked, so they go as they are. An item
// built from sub items sends its sizes alone ($withCutLength and $withRates false): it has no material of its own and
// never goes to cutting, turning or VMC, so its sub items carry the cut lengths and rates.
export const sizesPayload = (
  sizes: IItemSizeFormInput[] | undefined,
  withCutLength = true,
  withRates = true
) =>
  (sizes ?? []).map((size) => ({
    name: size.name,
    cutting_rate: withRates ? rateNumber(size.cutting_rate) : null,
    turning_rate: withRates ? rateNumber(size.turning_rate) : null,
    vmc_rate: withRates ? rateNumber(size.vmc_rate) : null,
    cut_length: withCutLength ? rateNumber(size.cut_length) : null,
  }));

// A typed cut length is more than 0; blank is fine.
export const validCutLength = (text: string | undefined) => {
  const length = rateNumber(text);
  return length === null || length > 0;
};
